import { YieldNestWithdrawalQueueAbi } from '../abis/yieldnest'
import type { LstWithdrawalRequest } from '../types'
import {
  computeStatus,
  toBigInt,
  toNumber,
  type LstWithdrawalReader,
} from './shared'

/**
 * YieldNest ynETH — ERC-721 enumerable withdrawal queue with
 * deterministic `secondsToFinalization` (a global value, applied to
 * every request's `creationTimestamp`).
 *
 * Three stages: `balanceOf` → per-index id → per-id detail. Plus a
 * one-time `secondsToFinalization()` for `readyAt` computation.
 */
export const readerYieldNest: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry) => {
    if (!entry.withdrawalContract) return []

    const stage1 = (await multicallRetry({
      chain: chainId,
      calls: [
        {
          address: entry.withdrawalContract,
          name: 'balanceOf',
          params: [user],
        },
        {
          address: entry.withdrawalContract,
          name: 'secondsToFinalization',
          params: [],
        },
      ] as unknown as never[],
      abi: [
        YieldNestWithdrawalQueueAbi,
        YieldNestWithdrawalQueueAbi,
      ] as unknown as never,
    })) as unknown[]
    const count = toNumber(stage1[0]) ?? 0
    const finalizationSeconds = toNumber(stage1[1]) ?? 0
    if (count === 0) return []

    const enumCalls = Array.from({ length: count }, (_, i) => ({
      address: entry.withdrawalContract!,
      name: 'tokenOfOwnerByIndex',
      params: [user, BigInt(i)],
    }))
    const stage2 = (await multicallRetry({
      chain: chainId,
      calls: enumCalls as unknown as never[],
      abi: enumCalls.map(() => YieldNestWithdrawalQueueAbi) as unknown as never,
    })) as unknown[]
    const ids = stage2.map(toBigInt).filter((v): v is bigint => v !== undefined)
    if (ids.length === 0) return []

    const detailCalls = ids.map((id) => ({
      address: entry.withdrawalContract!,
      name: 'withdrawalRequest',
      params: [id],
    }))
    const stage3 = (await multicallRetry({
      chain: chainId,
      calls: detailCalls as unknown as never[],
      abi: detailCalls.map(
        () => YieldNestWithdrawalQueueAbi,
      ) as unknown as never,
    })) as unknown[]

    const out: LstWithdrawalRequest[] = []
    for (let i = 0; i < ids.length; i++) {
      const id = ids[i]
      const r = stage3[i] as Record<string, unknown> | undefined
      if (!r) continue
      const amount = toBigInt(r.amount)
      const created = toNumber(r.creationTimestamp)
      const processed = Boolean(r.processed)
      if (amount === undefined || created === undefined) continue
      const readyAt = created + finalizationSeconds
      out.push({
        lst: entry.lst,
        brand: entry.brand,
        symbol: entry.symbol,
        requestId: id.toString(),
        amountUnderlying: amount.toString(),
        status: processed ? 'claimed' : computeStatus(readyAt),
        readyAt: processed ? undefined : readyAt,
      })
    }
    return out
  },
}
